import { useEffect } from 'react';
import { useLanguage } from '../../i18n/LocalizationContext';

const ENHANCED_DIALOG_CLASS = 'app-dialog-standardized';
const GENERATED_CLOSE_CLASS = 'app-dialog-generated-close';
const GENERATED_ACTIONS_CLASS = 'app-dialog-generated-actions';
const SKIP_TOP_CLOSE_ATTRIBUTE = 'data-app-dialog-skip-top-close';

const BACKDROP_BLOCKED_EVENTS = [
  'pointerdown',
  'mousedown',
  'mouseup',
  'click',
  'touchstart',
] as const;

const CLOSE_CONTROL_TEXT =
  /^(close|cancel|dismiss|back|no|बंद करें|रद्द करें|बंद करा|रद्द करा)$/i;

const isVisible = (element: HTMLElement) => {
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
};

const getButtonText = (button: HTMLButtonElement) =>
  [
    button.textContent,
    button.getAttribute('aria-label'),
    button.getAttribute('title'),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

const isCloseLikeButton = (button: HTMLButtonElement) => {
  if (button.classList.contains(GENERATED_CLOSE_CLASS)) return true;
  if (button.querySelector('[data-testid="CloseIcon"]')) return true;
  return CLOSE_CONTROL_TEXT.test(getButtonText(button));
};

const hasCloseLikeButton = (root: Element) =>
  Array.from(root.querySelectorAll<HTMLButtonElement>('button')).some((button) =>
    isVisible(button) && isCloseLikeButton(button)
  );

const findExistingCloseButton = (paper: Element, exclude?: HTMLButtonElement) =>
  Array.from(paper.querySelectorAll<HTMLButtonElement>('button')).find((button) =>
    button !== exclude &&
    !button.classList.contains(GENERATED_CLOSE_CLASS) &&
    isVisible(button) &&
    isCloseLikeButton(button)
  );

const dispatchEscapeKey = (paper: Element) => {
  const dialogRoot = paper.closest('.MuiDialog-root') ?? paper;
  const eventOptions = {
    key: 'Escape',
    code: 'Escape',
    bubbles: true,
    cancelable: true,
  };

  paper.dispatchEvent(new KeyboardEvent('keydown', eventOptions));
  dialogRoot.dispatchEvent(new KeyboardEvent('keydown', eventOptions));
  document.dispatchEvent(new KeyboardEvent('keydown', eventOptions));
};

const closeDialog = (paper: Element, sourceButton?: HTMLButtonElement) => {
  const existingCloseButton = findExistingCloseButton(paper, sourceButton);
  if (existingCloseButton) {
    existingCloseButton.click();
    return;
  }

  dispatchEscapeKey(paper);
};

const createCloseButton = (
  paper: Element,
  closeLabel: string,
  options: { variant: 'icon' | 'action' }
) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `${GENERATED_CLOSE_CLASS} app-dialog-generated-${options.variant}`;
  button.setAttribute('aria-label', closeLabel);
  button.textContent = options.variant === 'icon' ? '×' : closeLabel;

  if (options.variant === 'icon') {
    Object.assign(button.style, {
      position: 'absolute',
      top: '8px',
      right: '8px',
      width: '32px',
      height: '32px',
      border: '0',
      borderRadius: '50%',
      background: 'transparent',
      color: 'rgba(0, 0, 0, 0.65)',
      cursor: 'pointer',
      fontSize: '24px',
      lineHeight: '28px',
      padding: '0',
      zIndex: '2',
    });
  } else {
    Object.assign(button.style, {
      border: '1px solid rgba(102, 126, 234, 0.5)',
      borderRadius: '8px',
      background: '#fff',
      color: '#667eea',
      cursor: 'pointer',
      font: 'inherit',
      fontWeight: '500',
      minWidth: '72px',
      padding: '6px 16px',
      textTransform: 'none',
    });
  }

  button.addEventListener('click', () => closeDialog(paper, button));
  return button;
};

const hasTopRightCloseButton = (paper: HTMLElement, title: HTMLElement | null) => {
  if (title && hasCloseLikeButton(title)) return true;
  if (paper.querySelector('.app-dialog-generated-icon')) return true;

  return Array.from(paper.querySelectorAll<HTMLButtonElement>('button')).some((button) =>
    !button.closest('.MuiDialogActions-root') &&
    !button.classList.contains('app-dialog-generated-action') &&
    isVisible(button) &&
    isCloseLikeButton(button)
  );
};

const ensureTopRightClose = (paper: HTMLElement, closeLabel: string) => {
  const title = paper.querySelector<HTMLElement>('.MuiDialogTitle-root');
  if (hasTopRightCloseButton(paper, title)) return;

  paper.style.position = paper.style.position || 'relative';

  const target = title ?? paper;
  target.style.position = target.style.position || 'relative';

  if (title) {
    const currentPaddingRight = window.getComputedStyle(title).paddingRight;
    if (parseFloat(currentPaddingRight) < 56) {
      title.style.paddingRight = '56px';
    }
  }

  target.appendChild(createCloseButton(paper, closeLabel, { variant: 'icon' }));
};

const removeGeneratedTopRightClose = (paper: HTMLElement) => {
  paper
    .querySelectorAll<HTMLButtonElement>(`.${GENERATED_CLOSE_CLASS}.app-dialog-generated-icon`)
    .forEach((button) => button.remove());
};

const ensureBottomCloseAction = (paper: HTMLElement, closeLabel: string) => {
  let actions = paper.querySelector<HTMLElement>('.MuiDialogActions-root');
  if (actions && hasCloseLikeButton(actions)) return;

  if (!actions) {
    actions = document.createElement('div');
    actions.className = `MuiDialogActions-root MuiDialogActions-spacing ${GENERATED_ACTIONS_CLASS}`;
    Object.assign(actions.style, {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '8px',
      padding: '16px 24px',
    });
    paper.appendChild(actions);
  }

  if (!actions.querySelector(`.${GENERATED_CLOSE_CLASS}`)) {
    actions.insertBefore(createCloseButton(paper, closeLabel, { variant: 'action' }), actions.firstChild);
  }
};

const updateGeneratedCloseLabels = (paper: HTMLElement, closeLabel: string) => {
  paper
    .querySelectorAll<HTMLButtonElement>(`.${GENERATED_CLOSE_CLASS}`)
    .forEach((button) => {
      button.setAttribute('aria-label', closeLabel);
      if (button.classList.contains('app-dialog-generated-action')) {
        button.textContent = closeLabel;
      }
    });
};

const blockBackdropClose = (event: Event) => {
  const target = event.target as Element | null;
  if (!target) return;

  const dialogRoot = target.closest('.MuiDialog-root');
  if (!dialogRoot) return;

  if (target.closest('.MuiDialog-paper')) return;

  if (target.closest('.MuiBackdrop-root') || target.closest('.MuiDialog-container')) {
    event.preventDefault();
    event.stopPropagation();
    if ('stopImmediatePropagation' in event) {
      event.stopImmediatePropagation();
    }
  }
};

const enhanceOpenDialogs = (closeLabel: string) => {
  document
    .querySelectorAll<HTMLElement>('.MuiDialog-root .MuiDialog-paper')
    .forEach((paper) => {
      updateGeneratedCloseLabels(paper, closeLabel);
      if (paper.hasAttribute(SKIP_TOP_CLOSE_ATTRIBUTE)) {
        removeGeneratedTopRightClose(paper);
      } else {
        ensureTopRightClose(paper, closeLabel);
      }
      ensureBottomCloseAction(paper, closeLabel);
      paper.classList.add(ENHANCED_DIALOG_CLASS);
    });
};

const DialogBehaviorGuard = () => {
  const { t } = useLanguage();
  const closeLabel = t('common.close');

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    BACKDROP_BLOCKED_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, blockBackdropClose, true);
    });

    const scheduleEnhance = () => window.requestAnimationFrame(() => enhanceOpenDialogs(closeLabel));
    const observer = new MutationObserver(scheduleEnhance);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    scheduleEnhance();

    return () => {
      observer.disconnect();
      BACKDROP_BLOCKED_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, blockBackdropClose, true);
      });
    };
  }, [closeLabel]);

  return null;
};

export default DialogBehaviorGuard;
